/**
 * WorkflowHeader — the slim top bar of a focused, full-screen task workflow
 * (production records, the cafeteria wizards, Menus' Packaging data). Title
 * and subtitle on the left; caller-supplied actions (Save for later, etc.) on
 * the right; an optional `center` slot between them.
 *
 * The row is a 1fr/auto/1fr grid: the equal side tracks are what centre the
 * `center` slot against the header rather than against whatever the title
 * happens to be, and the middle track always exists, so the actions keep the
 * same x-position from screen to screen. `center` sits outside the
 * `leading ?? (…)` fallback because `leading` REPLACES the title block: a
 * centred element rendered inside it would silently vanish whenever a caller
 * passes `leading`.
 *
 * Promoted from Ordo's `CafeteriaWorkflowHeader` (Oct 19, 2026) with the
 * same markup; that header is now a thin wrapper that fills `center` with
 * the campus and prefixes `actions` with its pending-charges pill.
 */

import type { ReactNode } from 'react'

export interface WorkflowHeaderProps {
  title?: string
  /** The second line under the title: what the workflow acts on (a campus and a day, a last push). */
  subtitle?: string
  /** Replaces the title block entirely (POS-style chrome, such as an X close button). */
  leading?: ReactNode
  /** The middle track, centred against the whole header. */
  center?: ReactNode
  actions?: ReactNode
}

export function WorkflowHeader({ title, subtitle, leading, center, actions }: WorkflowHeaderProps) {
  return (
    <header className="sticky top-0 z-10 grid grid-cols-[1fr_auto_1fr] items-center gap-3 border-b bg-background px-4 py-4 md:px-6">
      <div className="flex min-w-0 items-center gap-3">
        {leading ?? (
          <div className="min-w-0">
            <h1 className="truncate text-lg font-semibold tracking-normal">{title}</h1>
            {subtitle ? <p className="truncate text-sm text-muted-foreground">{subtitle}</p> : null}
          </div>
        )}
      </div>
      {/* Always occupies the middle track, so the actions stay in the third one
          and a centred element keeps the same x-position from screen to screen. */}
      <div className="flex min-w-0 justify-center">{center}</div>
      <div className="flex shrink-0 items-center justify-end gap-2">{actions}</div>
    </header>
  )
}
